import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/fmp_landing.css';
import '../styles/solicitacao_admissao.css';
import logoComece from '../assets/logo-comece.svg';

// Página PÚBLICA (sem login) — substitui o Google Forms de admissão.
// O RH do cliente preenche e a solicitação cai em solicitacao_admissao,
// aparecendo na página de Admissões do BPO RH.

const FORM_INICIAL = {
  // respostas das perguntas próprias da empresa (ex.: BBDA — dias presenciais)
  perguntas_extra: {},
  email_solicitante: '',
  cnpj: '',
  empresa: '',
  colaborador: '',
  departamento: '',
  centro_custo: '',
  data_admissao: '',
  salario: '',
  funcao: '',
  categoria_trabalhador: '',
  periodo_experiencia: '',
  email_colaborador: '',
  telefone_colaborador: '',
  gestor: '',
  horario_trabalho: '',
  jornada_trabalho: '',
  horas_mensais: '',
  filial: '',
  informacoes_adicionais: '',
  website: '', // honeypot anti-bot — humano não vê nem preenche
};

const OPCOES_VAZIAS = {
  encontrado: false, empresa: '', cargos: [], departamentos: [],
  centros_custo: [], jornadas: [], periodos_experiencia: [], vinculos: [],
  perguntas_extra: [],
};

const mascararCnpj = (v) => {
  const d = String(v || '').replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

// Telefone no formato que a Convenia aceita: DDD + número, máx. 11 dígitos
// ('cell' é limitado a 11 caracteres lá). Descarta o 55 do código do país se
// a pessoa colar '+55 11 91479-3302' e trava o input em 11 dígitos.
const mascararTelefone = (v) => {
  let d = String(v || '').replace(/\D/g, '');
  if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
  d = d.slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// '200:00' -> '200 h/mês'
const rotuloJornada = (j) => `${String(j).split(':')[0]} h/mês`;

// Definido FORA do componente: identidade estável entre renders
// (dentro, o React remontaria o input a cada tecla e o foco se perderia).
const Campo = ({ id, rotulo, icone, obrigatorio, ajuda, children }) => (
  <div className={`sa-field ${id === 'informacoes_adicionais' ? 'full' : ''}`}>
    <label htmlFor={id}>
      {icone && <i className={`fas ${icone}`} aria-hidden="true"></i>}
      {rotulo} {obrigatorio && <span className="req">*</span>}
    </label>
    {children}
    {ajuda && (
      <small style={{ color: '#6c757d', fontSize: '.78rem' }}>{ajuda}</small>
    )}
  </div>
);

// Também FORA do componente, pelo mesmo motivo que `Campo`: identidade estável
// entre renders. Vira select quando a empresa tem o cadastro sincronizado e
// texto livre quando não há opção (empresa nova, sem colaboradores ainda).
const Selecao = ({ id, valor, onChange, lista, placeholder, comCadastro, formatar }) => (
  lista.length > 0 ? (
    <select id={id} required value={valor} onChange={onChange}>
      <option value="">Selecione...</option>
      {lista.map((o) => (
        <option key={String(o)} value={String(o)}>{formatar ? formatar(o) : o}</option>
      ))}
    </select>
  ) : (
    <input id={id} type="text" required value={valor} onChange={onChange}
      placeholder={comCadastro ? 'Sem opções cadastradas — digite' : placeholder} />
  )
);

const SolicitacaoAdmissao = () => {
  const [form, setForm] = useState(FORM_INICIAL);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState('');

  // Opções do cadastro DA EMPRESA, buscadas pelo CNPJ. Cargo, departamento,
  // centro de custo, jornada e período de experiência são entidades do cadastro
  // de cada empresa na Convenia — texto livre é descartado na importação.
  const [opcoes, setOpcoes] = useState(OPCOES_VAZIAS);
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);
  const [erroCnpj, setErroCnpj] = useState('');

  const digitosCnpj = form.cnpj.replace(/\D/g, '');

  useEffect(() => {
    if (digitosCnpj.length !== 14) {
      setOpcoes(OPCOES_VAZIAS);
      setErroCnpj('');
      return;
    }
    let cancelado = false;
    setBuscandoCnpj(true);
    setErroCnpj('');
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/admissao/opcoes/`, { params: { cnpj: digitosCnpj } })
      .then((res) => {
        if (cancelado) return;
        setOpcoes({ ...OPCOES_VAZIAS, ...res.data, encontrado: true });
        // o nome da empresa passa a vir do cadastro, não da digitação
        setForm((f) => ({ ...f, empresa: res.data.empresa || f.empresa }));
      })
      .catch((err) => {
        if (cancelado) return;
        setOpcoes(OPCOES_VAZIAS);
        setErroCnpj(err.response?.data?.mensagem
          || 'Não foi possível validar o CNPJ. Tente novamente.');
      })
      .finally(() => { if (!cancelado) setBuscandoCnpj(false); });
    return () => { cancelado = true; };
  }, [digitosCnpj]);

  const setCampo = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
  const setExtra = (campo) => (e) => setForm((f) => ({
    ...f, perguntas_extra: { ...f.perguntas_extra, [campo]: e.target.value } }));

  const setCnpj = (e) => setForm((f) => ({ ...f, cnpj: mascararCnpj(e.target.value) }));

  const setTelefone = (e) =>
    setForm((f) => ({ ...f, telefone_colaborador: mascararTelefone(e.target.value) }));


  const enviar = async (e) => {
    e.preventDefault();
    setErro('');
    // sem empresa identificada a solicitação chega órfã e a operadora tem de
    // descobrir o cliente na mão — barra aqui, não depois
    if (!opcoes.encontrado) {
      setErro(digitosCnpj.length === 14
        ? 'Não identificamos esse CNPJ. Confira o número ou fale com a Comece Hub.'
        : 'Informe o CNPJ da empresa para continuar.');
      return;
    }
    // A Convenia exige 11 dígitos no celular (DDD + 9 + 8 dígitos) e RECUSA a
    // admissão inteira quando vem diferente — número antigo de 10 dígitos
    // derrubava o cadastro lá na frente, então barra aqui.
    const telDigitos = form.telefone_colaborador.replace(/\D/g, '');
    if (telDigitos.length !== 11) {
      setErro(telDigitos.length === 10
        ? 'Celular com 10 dígitos — falta o 9 depois do DDD. Ex.: (54) 99621-0919.'
        : 'Informe o celular com DDD e 9 dígitos, ex.: (11) 91479-3302.');
      return;
    }
    setEnviando(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/solicitacao-admissao/`,
        form
      );
      if (res.data?.success) {
        setEnviado(true);
      } else {
        setErro(res.data?.mensagem || 'Não foi possível enviar. Tente novamente.');
      }
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Não foi possível enviar. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="fmp-site">
      {/* ── Topo ── */}
      <header className="sa-hero">
        <div className="sa-wrap">
          <img src={logoComece} alt="Comece Hub" style={{ height: 34, display: 'block', margin: '0 auto 6px' }} />
          <h1>Solicitação de Admissão</h1>
          <p>
            Preencha os dados do novo colaborador. A equipe de <b style={{ color: '#fff' }}>BPO de RH da
            Comece Hub</b> recebe a solicitação na hora e conduz a admissão.
          </p>
        </div>
      </header>

      <div className="sa-wrap">
        <div className="sa-card">
          {enviado ? (
            <div className="sa-sucesso">
              <div className="ic"><i className="fas fa-check"></i></div>
              <h2>Solicitação enviada!</h2>
              <p>
                Recebemos os dados de <b>{form.colaborador}</b>. Nossa equipe de RH vai
                iniciar o processo de admissão e entrará em contato se precisar de algo.
              </p>
              <button
                className="sa-btn"
                onClick={() => { setForm(FORM_INICIAL); setEnviado(false); }}
              >
                <i className="fas fa-plus"></i> Enviar outra admissão
              </button>
            </div>
          ) : (
            <form onSubmit={enviar}>
              <h2><i className="fas fa-user-plus" style={{ color: 'var(--cyan)', marginRight: 10 }}></i>Dados da admissão</h2>
              <div className="sa-sub">Campos com <span style={{ color: 'var(--danger)' }}>*</span> são obrigatórios.</div>

              {erro && <div className="sa-erro"><i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>{erro}</div>}

              {/* honeypot anti-bot */}
              <div className="sa-hp" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input id="website" type="text" tabIndex={-1} autoComplete="off"
                  value={form.website} onChange={setCampo('website')} />
              </div>

              <div className="sa-grid">
                <Campo id="email_solicitante" rotulo="Seu e-mail" icone="fa-envelope" obrigatorio>
                  <input id="email_solicitante" type="email" required placeholder="voce@empresa.com.br"
                    value={form.email_solicitante} onChange={setCampo('email_solicitante')} />
                </Campo>

                <Campo id="cnpj" rotulo="CNPJ da empresa" icone="fa-id-card" obrigatorio>
                  <input id="cnpj" type="text" required placeholder="00.000.000/0000-00"
                    inputMode="numeric" value={form.cnpj} onChange={setCnpj} />
                  {buscandoCnpj && (
                    <span className="sa-cnpj-status">
                      <i className="fas fa-circle-notch fa-spin"></i> Buscando a empresa...
                    </span>
                  )}
                  {!buscandoCnpj && opcoes.encontrado && (
                    <span className="sa-cnpj-status ok">
                      <i className="fas fa-circle-check"></i> {opcoes.empresa}
                    </span>
                  )}
                  {!buscandoCnpj && erroCnpj && (
                    <span className="sa-cnpj-status erro">
                      <i className="fas fa-circle-exclamation"></i> {erroCnpj}
                    </span>
                  )}
                  {!form.cnpj && (
                    <span className="sa-cnpj-status">
                      O CNPJ carrega os cargos, departamentos e jornadas da sua empresa.
                    </span>
                  )}
                </Campo>

                <div className="sa-field full">
                  <label htmlFor="colaborador">
                    <i className="fas fa-user" aria-hidden="true"></i>
                    Nome completo do colaborador <span className="req">*</span>
                  </label>
                  <input id="colaborador" type="text" required placeholder="Nome e sobrenome"
                    value={form.colaborador} onChange={setCampo('colaborador')} />
                </div>

                <Campo id="departamento" rotulo="Departamento" icone="fa-sitemap" obrigatorio>
                  <Selecao id="departamento" valor={form.departamento} onChange={setCampo('departamento')}
                    lista={opcoes.departamentos} comCadastro={opcoes.encontrado}
                    placeholder="Informe o CNPJ para carregar as opções" />
                </Campo>

                <Campo id="centro_custo" rotulo="Centro de custo" icone="fa-coins" obrigatorio>
                  <Selecao id="centro_custo" valor={form.centro_custo} onChange={setCampo('centro_custo')}
                    lista={opcoes.centros_custo} comCadastro={opcoes.encontrado}
                    placeholder="Informe o CNPJ para carregar as opções" />
                </Campo>

                <Campo id="data_admissao" rotulo="Data de admissão" icone="fa-calendar-plus" obrigatorio>
                  <input id="data_admissao" type="date" required
                    value={form.data_admissao} onChange={setCampo('data_admissao')} />
                </Campo>

                <Campo id="salario" rotulo="Salário" icone="fa-money-bill-wave" obrigatorio>
                  <input id="salario" type="text" required placeholder="Ex: R$ 3.500,00"
                    value={form.salario} onChange={setCampo('salario')} />
                </Campo>

                <Campo id="funcao" rotulo="Função (cargo)" icone="fa-briefcase" obrigatorio>
                  <Selecao id="funcao" valor={form.funcao} onChange={setCampo('funcao')}
                    lista={opcoes.cargos} comCadastro={opcoes.encontrado}
                    placeholder="Informe o CNPJ para carregar as opções" />
                </Campo>

                <Campo id="email_colaborador" rotulo="E-mail pessoal do colaborador" icone="fa-at" obrigatorio>
                  <input id="email_colaborador" type="email" required placeholder="colaborador@email.com"
                    value={form.email_colaborador} onChange={setCampo('email_colaborador')} />
                </Campo>

                <Campo id="telefone_colaborador" rotulo="Telefone do colaborador"
                  icone="fa-mobile-alt" obrigatorio
                  ajuda="Celular com DDD e 9 dígitos — é o formato que a Convenia aceita">
                  <input id="telefone_colaborador" type="tel" required
                    placeholder="(00) 90000-0000" minLength={15}
                    title="Celular com DDD e 9 dígitos"
                    inputMode="numeric" maxLength={15} autoComplete="tel-national"
                    value={form.telefone_colaborador} onChange={setTelefone} />
                </Campo>

                <Campo id="gestor" rotulo="Gestor do colaborador" icone="fa-user-tie" obrigatorio>
                  <input id="gestor" type="text" required placeholder="Nome do gestor direto"
                    value={form.gestor} onChange={setCampo('gestor')} />
                </Campo>

                <Campo id="horario_trabalho" rotulo="Horário de trabalho" icone="fa-clock" obrigatorio>
                  <select id="horario_trabalho" required
                    value={form.horario_trabalho} onChange={setCampo('horario_trabalho')}>
                    <option value="">Selecione...</option>
                    {['08:00 às 17:00', '08:00 às 18:00', '08:30 às 17:30', '09:00 às 18:00',
                      '09:00 às 19:00', '10:00 às 19:00',
                      'Meio período — manhã', 'Meio período — tarde',
                      'Turno livre', 'Escala (6x1)', 'Escala (12x36)']
                      .map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Campo>

                <Campo id="filial" rotulo="Alocado em qual filial?" icone="fa-map-marker-alt" obrigatorio>
                  <input id="filial" type="text" required list="sa-filiais" placeholder="Ex: Matriz"
                    value={form.filial} onChange={setCampo('filial')} />
                  <datalist id="sa-filiais">
                    {['Matriz', 'Filial Lavras', 'Filial SBC', 'Filial Nova Lima'].map(o => <option key={o} value={o} />)}
                  </datalist>
                </Campo>

                <Campo id="horas_mensais" rotulo="Jornada (horas mensais)" icone="fa-business-time" obrigatorio>
                  <Selecao id="horas_mensais" valor={form.horas_mensais} onChange={setCampo('horas_mensais')}
                    lista={opcoes.jornadas} comCadastro={opcoes.encontrado}
                    formatar={rotuloJornada}
                    placeholder="Informe o CNPJ para carregar as opções" />
                </Campo>

                <Campo id="categoria_trabalhador" rotulo="Vínculo" icone="fa-file-contract" obrigatorio>
                  <select id="categoria_trabalhador" required
                    value={form.categoria_trabalhador} onChange={setCampo('categoria_trabalhador')}>
                    <option value="">Selecione...</option>
                    {opcoes.vinculos.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Campo>

                <Campo id="periodo_experiencia" rotulo="Período de experiência" icone="fa-hourglass-half" obrigatorio>
                  <Selecao id="periodo_experiencia" valor={form.periodo_experiencia}
                    onChange={setCampo('periodo_experiencia')}
                    lista={opcoes.periodos_experiencia} comCadastro={opcoes.encontrado}
                    placeholder="Informe o CNPJ para carregar as opções" />
                </Campo>

                {(opcoes.perguntas_extra || []).map((pg) => (
                  <div className="sa-field full" key={pg.campo}>
                    <label htmlFor={`extra_${pg.campo}`}>
                      <i className="fas fa-building-user" aria-hidden="true"></i>
                      {pg.rotulo} <span className="req">*</span>
                    </label>
                    <select id={`extra_${pg.campo}`} required
                      value={form.perguntas_extra[pg.campo] || ''} onChange={setExtra(pg.campo)}>
                      <option value="">Selecione...</option>
                      {pg.opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}

                <div className="sa-field full">
                  <label htmlFor="informacoes_adicionais">
                    <i className="fas fa-comment-dots" aria-hidden="true"></i>
                    Informações adicionais
                  </label>
                  <textarea id="informacoes_adicionais"
                    placeholder="Benefícios específicos, observações, particularidades do contrato..."
                    value={form.informacoes_adicionais} onChange={setCampo('informacoes_adicionais')} />
                </div>
              </div>

              <div className="sa-actions">
                <button type="submit" className="sa-btn" disabled={enviando}>
                  {enviando
                    ? (<><i className="fas fa-circle-notch fa-spin"></i> Enviando...</>)
                    : (<>Enviar solicitação <i className="fas fa-paper-plane"></i></>)}
                </button>
                <span className="sa-note">
                  <i className="fas fa-lock" style={{ marginRight: 6 }}></i>
                  Os dados são usados exclusivamente para o processo de admissão.
                </span>
              </div>
            </form>
          )}
        </div>
      </div>

      <footer className="sa-footer">
        <b>Comece Hub</b> · BPO de RH — Contabilidade consultiva · BPO Financeiro · BI
      </footer>
    </div>
  );
};

export default SolicitacaoAdmissao;
